import { useMemo } from 'react';

type AccountType = {
  _id: string;
  accountType?: string;
  accountTag?: string;
  isAccountHolder?: boolean;
  isPrepaid?: boolean;
  isTaxAllowed?: boolean;
};

type Props = {
  existingAccountsValues: AccountType[];
  selectedAccountId: string;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  signupToken?: string;
  countryId?: string;
};

export const useExistingAccount = ({
  existingAccountsValues,
  selectedAccountId,
  setFieldValue,
  signupToken,
  countryId,
}: Props) => {
  const selectedAccount = useMemo(() => {
    return existingAccountsValues.find(acc => acc._id === selectedAccountId);
  }, [selectedAccountId, existingAccountsValues]);

  const applyAccountValues = (accountId: string) => {
    const acc = existingAccountsValues.find(a => a._id === accountId);
    if (!acc) return;

    setFieldValue('accountType', acc.accountType || 'individual', false);
    setFieldValue('accountTag', acc.accountTag || '', false);
    setFieldValue('isAccountHolder', acc.isAccountHolder ?? false, false);
    setFieldValue('isPrepaid', acc.isPrepaid ?? true, false);
    setFieldValue('isTaxAllowed', acc.isTaxAllowed ?? false, false);
  };

  const resetAccountValues = () => {
    setFieldValue('accountType', 'individual', false);
    setFieldValue('accountTag', '', false);
    setFieldValue('isAccountHolder', false, false);
    setFieldValue('isPrepaid', true, false);
    setFieldValue('isTaxAllowed', false, false);
  };

  const isExistingAccountSelected = !!selectedAccountId;

  const isFieldDisabled = useMemo(() => {
    // 👉 if signupToken and countryId both exist
    // then fields should NOT be disabled
    if (signupToken && countryId) return false;

    if (!selectedAccount) return false;

    // 👉 individual case → never disable
    if (selectedAccount.accountType === 'individual') return false;

    // 👉 company case → disable
    return true;
  }, [selectedAccount, signupToken, countryId]);

  return {
    selectedAccount,
    applyAccountValues,
    resetAccountValues,
    isExistingAccountSelected,
    isFieldDisabled,
  };
};
